import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAfter, isBefore } from 'date-fns'
import {
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'

import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssueEntity } from 'types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'
import { categorizeIssues } from '../../../../components/utils/categorizeIssues'
import { isFutureWarrant, isPreviousWarrant } from '../../../warrants/WarrantsView'
import { findSubscriptionPeriodCloseToDate } from '../../../../utils/warrants'
import { useLoaderData } from 'react-router'
import useAuth from '../../../../hooks/useAuth'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'
import { CompanyEntity } from 'types/CompanyEntity'
import InfoBlock from '../../../components/InfoBlock'
import { IssueTableRow } from './IssueTableRow'
import { WarrantTableRow } from './WarrantTableRow'
import ConvertibleTableRow from './ConvertibleTableRow'

export type TransactionObject =
    | {
          type: 'issue'
          subStartDate: string
          subEndDate: string
          item: IssueEntity
      }
    | {
          type: 'warrant'
          subStartDate: string
          subEndDate: string
          item: WarrantEntity
      }
    | {
          type: 'convertible'
          date: string
          item: ConvertibleEntity
      }

type CurrentTransactionsComponent = React.FC<{
    convertibles: ConvertibleEntity[]
}>

function filterCombineAndSort(issues: IssueEntity[], warrants: WarrantEntity[], convertibles: ConvertibleEntity[]) {
    const today = new Date()

    const filteredIssues = issues.filter((issue) => issue.publishedAt && !hasSourceOfType(issue, 'Cancellation'))

    const { currentIssues, futureIssues } = categorizeIssues(filteredIssues)

    // Filter warrants to keep future and current warrants
    let filteredWarrants = warrants.filter((warrant) => {
        return !warrant.cancelled && (isFutureWarrant(today, warrant) || !isPreviousWarrant(today, warrant))
    })

    filteredWarrants = filteredWarrants.filter((warrant) => !warrant.cancelled)

    // Combine the filtered issues and warrants into a single array
    const transactions = [
        ...currentIssues.map((issue) => {
            return {
                type: 'issue',
                subStartDate:
                    issue.subStartDate ||
                    issue.sources.filter((source) => source.sourceType === 'Publication')[0].publishedDate,
                subEndDate: issue.subEndDate || null,
                item: issue,
            }
        }),
        ...futureIssues.map((issue) => {
            return {
                type: 'issue',
                subStartDate:
                    issue.subStartDate ||
                    issue.sources.filter((source) => source.sourceType === 'Publication')[0].publishedDate,
                subEndDate: issue.subEndDate || null,
                item: issue,
            }
        }),
        ...filteredWarrants.map((warrant) => {
            const now = new Date().toISOString()
            const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, now)

            if (currentPeriod) {
                warrant.currentSubscriptionPeriod = currentPeriod
            }

            const sortedSubscriptionPeriods = warrant.subscriptionPeriods
                .filter((sp) => isAfter(sp.subEndDate, new Date()))
                .sort((a, b) => (isBefore(a.subEndDate, b.subEndDate) ? 1 : -1))

            return {
                type: 'warrant',
                subStartDate: currentPeriod ? currentPeriod.subStartDate : null,
                subEndDate:
                    sortedSubscriptionPeriods.length > 0
                        ? sortedSubscriptionPeriods[0].subEndDate
                        : currentPeriod
                          ? currentPeriod.subEndDate
                          : null,
                item: warrant,
            }
        }),
        ...convertibles.map((convertible) => {
            return {
                type: 'convertible',
                date: convertible.maturityDate,
                item: convertible,
            }
        }),
    ]

    transactions.sort((a, b) => {
        const dateA = a.type === 'convertible' ? a.date : a.subStartDate
        const dateB = b.type === 'convertible' ? b.date : b.subStartDate
        if (dateA === null) return 1
        if (dateB === null) return -1

        const dateComparison = new Date(dateA) - new Date(dateB)
        if (dateComparison !== 0) return dateComparison

        const name1 = a.item.instrumentName || a.item.name || ''
        const name2 = b.item.instrumentName || b.item.name || ''
        return name1.localeCompare(name2, 'sv')
    })

    return transactions
}

export const CurrentTransactions: CurrentTransactionsComponent = ({ convertibles }) => {
    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [warrants, setWarrants] = useState(null)
    const [transactions, setTransactions] = useState([])
    const [includeCompletedConvertibles, setIncludeCompletedConvertibles] = useState(false)

    const company = useLoaderData()
    const { isAuthenticated } = useAuth()
    const theme = useTheme()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
    const { i18n } = useTranslation()
    const locale = i18n.language

    const issueApi = new IssueApi()
    const warrantApi = new WarrantApi()

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            try {
                const fetchedIssues = await issueApi.findByCompanyId(company.id)

                fetchedIssues.forEach((issue) => {
                    const now = new Date().toISOString()
                    const currentPeriod = findSubscriptionPeriodCloseToDate(issue, now)
                    if (currentPeriod) {
                        issue.currentSubscriptionPeriod = currentPeriod
                        issue.subStartDate = currentPeriod.subStartDate
                        issue.subEndDate = currentPeriod.subEndDate
                    }
                })

                setIssues(fetchedIssues)
            } catch (e) {
                console.error(e)
            }
        }
        void fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, company])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            try {
                const fetchedWarrants = await warrantApi.findByCompanyId(company.id)

                fetchedWarrants.forEach((warrant) => {
                    const now = new Date().toISOString()
                    const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, now)
                    const sortedSubscriptionPeriods = warrant.subscriptionPeriods
                        .filter((sp) => isAfter(sp.subEndDate, new Date()))
                        .sort((a, b) => (isBefore(a.subEndDate, b.subEndDate) ? 1 : -1))

                    if (currentPeriod) {
                        warrant.currentSubscriptionPeriod = currentPeriod
                        warrant.subStartDate = currentPeriod.subStartDate
                        warrant.subEndDate =
                            sortedSubscriptionPeriods.length > 0
                                ? sortedSubscriptionPeriods[0].subEndDate
                                : currentPeriod.subEndDate
                    }
                })

                setWarrants(fetchedWarrants)
            } catch (e) {
                console.error(e)
            }
        }
        void fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, company])

    useEffect(() => {
        if (!warrants || !issues) return

        setTransactions(filterCombineAndSort(issues, warrants, convertibles))
    }, [warrants, issues, convertibles])

    let content = null

    const anyCompletedConvertibles = convertibles.some((convertibles) => convertibles.completed)

    const multipleInstrumentsExists =
        company?.shares.length > 1 && transactions.filter((transaction) => transaction.type === 'issue').length > 0

    if (transactions.length === 0) {
        content = (
            <Typography>
                <i>Inga pågående eller kommande emissioner, teckningsoptioner eller konvertibellån.</i>
            </Typography>
        )
    } else if (company && transactions.length > 0) {
        content = (
            <Table sx={!isLargeScreen ? { marginX: -3 } : null}>
                <TableHead>
                    <TableRow>
                        {multipleInstrumentsExists && isLargeScreen ? <TableCell>Aktie</TableCell> : null}
                        <TableCell>Typ</TableCell>
                        <TableCell>Datum</TableCell>
                        <TableCell>Teckningskurs</TableCell>
                        {isLargeScreen ? <TableCell>Belopp</TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {transactions
                        .filter((transaction) => {
                            if (transaction.type !== 'convertible') return true

                            return includeCompletedConvertibles ? true : !transaction.item.completed
                        })
                        .map((obj, index) =>
                            obj.type === 'issue' ? (
                                <IssueTableRow
                                    key={obj.item.id + index}
                                    obj={obj}
                                    locale={locale}
                                    isLargeScreen={isLargeScreen}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                />
                            ) : obj.type === 'warrant' ? (
                                <WarrantTableRow
                                    key={obj.item.id + index}
                                    obj={obj}
                                    locale={locale}
                                    isLargeScreen={isLargeScreen}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                />
                            ) : (
                                <ConvertibleTableRow
                                    key={obj.item.id + index}
                                    obj={obj}
                                    isLargeScreen={isLargeScreen}
                                    multipleInstrumentsExists={multipleInstrumentsExists}
                                />
                            ),
                        )}
                </TableBody>
            </Table>
        )
    }

    return (
        <InfoBlock
            title={
                <>
                    Aktuella emissioner, TO och konvertibellån
                    {anyCompletedConvertibles ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeCompletedConvertibles}
                                    onChange={() => setIncludeCompletedConvertibles((prev) => !prev)}
                                    disableRipple
                                />
                            }
                            label='Visa avslutade konvertibellån'
                            sx={{ pl: 6 }}
                        />
                    ) : null}
                </>
            }
            loadingElement={!company}
        >
            {content}
        </InfoBlock>
    )
}
